import type { ActivityLog, Category, Order, OrderItem, Payment, Product, Reservation, StationKey, StationPrinterConfig, StationTicket, Table, User, Zone } from '@/lib/pos-types'

/**
 * Normalizadores y helpers de parsing para la API.
 *
 * Objetivo:
 * - Aislar el resto de la UI de:
 *   - respuestas con envelope `{ success, message, data }`
 *   - diferencias `snake_case` vs `camelCase`
 *   - fechas en string (se convierten a `Date`)
 *
 * Convención:
 * - `unwrapList/unwrapSingle` extraen `data` si existe.
 * - `normalizeX` devuelve el tipo de dominio usado por el frontend (pos-types).
 */
export const unwrapList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: unknown[] }).data
  }
  return []
}

export const unwrapSingle = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data
  }
  return payload
}

type JsonObject = Record<string, unknown>

const asObject = (value: unknown): JsonObject => {
  if (value && typeof value === 'object') {
    const obj = value as JsonObject
    const inner = obj.data
    if (inner && typeof inner === 'object' && !Array.isArray(inner) && ('success' in obj || 'message' in obj)) {
      return inner as JsonObject
    }
    return obj
  }
  return {}
}

const getFirst = (obj: JsonObject, keys: string[]) => {
  for (const key of keys) {
    if (obj[key] !== undefined) return obj[key]
  }
  return undefined
}

const asString = (value: unknown, fallback = '') => (value === null || value === undefined ? fallback : String(value))

const asOptionalString = (value: unknown): string | undefined =>
  value === null || value === undefined || value === '' ? undefined : String(value)

const asNumber = (value: unknown, fallback = 0) => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

const asOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : undefined
}

const asBoolean = (value: unknown, fallback = false) => {
  if (typeof value === 'boolean') return value
  if (value === null || value === undefined) return fallback
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return ['true', '1', 'yes', 'y', 'si', 'sí'].includes(value.toLowerCase())
  return fallback
}

const normalizeStatus = (value: unknown) => String(value ?? '').replace(/_/g, '-')

const toOptionalDate = (value: unknown): Date | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  if (value instanceof Date) return value
  const d = new Date(String(value))
  return Number.isNaN(d.getTime()) ? undefined : d
}

const toDate = (value: unknown): Date => toOptionalDate(value) ?? new Date(0)

export const normalizeTable = (raw: unknown): Table => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    number: asNumber(getFirst(obj, ['number'])),
    capacity: asNumber(getFirst(obj, ['capacity'])),
    status: normalizeStatus(getFirst(obj, ['status'])) as Table['status'],
    zone: asString(getFirst(obj, ['zone'])),
    layoutGx: asNumber(getFirst(obj, ['layoutGx', 'layout_gx']), 0),
    layoutGy: asNumber(getFirst(obj, ['layoutGy', 'layout_gy']), 0),
    layoutSize: asNumber(getFirst(obj, ['layoutSize', 'layout_size']), 1) as Table['layoutSize'],
    layoutShape: asString(getFirst(obj, ['layoutShape', 'layout_shape']), 'square') as Table['layoutShape'],
    currentOrderId: asOptionalString(getFirst(obj, ['currentOrderId', 'current_order_id'])),
    waiter: asOptionalString(getFirst(obj, ['waiter'])),
    openedAt: toOptionalDate(getFirst(obj, ['openedAt', 'opened_at'])),
    blockedReason: asOptionalString(getFirst(obj, ['blockedReason', 'blocked_reason', 'block_reason', 'motivo_bloqueo'])),
  }
}

export const normalizeZone = (raw: unknown): Zone => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    name: asString(getFirst(obj, ['name'])),
    order: asNumber(getFirst(obj, ['order', 'sort_order'])),
    layoutWidth: asNumber(getFirst(obj, ['layoutWidth', 'layout_width']), 620),
    layoutHeight: asNumber(getFirst(obj, ['layoutHeight', 'layout_height']), 420),
  }
}

export const normalizeCategory = (raw: unknown): Category => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    name: asString(getFirst(obj, ['name'])),
    icon: asString(getFirst(obj, ['icon'])),
    order: asNumber(getFirst(obj, ['order', 'sort_order'])),
    stationKey: asOptionalString(getFirst(obj, ['stationKey', 'station_key'])) as Category['stationKey'],
  }
}

export const normalizeProduct = (raw: unknown): Product => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    name: asString(getFirst(obj, ['name'])),
    description: asString(getFirst(obj, ['description'])),
    price: asNumber(getFirst(obj, ['price'])),
    categoryId: asString(getFirst(obj, ['categoryId', 'category_id'])),
    stationKey: asOptionalString(getFirst(obj, ['stationKey', 'station_key'])) as Product['stationKey'],
    image: asOptionalString(getFirst(obj, ['image'])),
    available: asBoolean(getFirst(obj, ['available']), true),
    stockAvailable: asBoolean(getFirst(obj, ['stockAvailable', 'stock_available']), true),
    stockPortions: (() => {
      const v = getFirst(obj, ['stockPortions', 'stock_portions'])
      if (v === null || v === undefined) return null
      const n = Number(v)
      return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null
    })(),
    preparationTime: asOptionalNumber(getFirst(obj, ['preparationTime', 'preparation_time'])),
    availabilityRules: Array.isArray(getFirst(obj, ['availabilityRules', 'availability_rules'])) ? (getFirst(obj, ['availabilityRules', 'availability_rules']) as unknown[]).map((r) => asObject(r)).map((o) => ({
      days: Array.isArray(o.days) ? (o.days as unknown[]).map((d) => Number(d)).filter((n) => Number.isFinite(n)) : [],
      start: asString(o.start),
      end: asString(o.end),
    })) : undefined,
    priceRules: Array.isArray(getFirst(obj, ['priceRules', 'price_rules'])) ? (getFirst(obj, ['priceRules', 'price_rules']) as unknown[]).map((r) => asObject(r)).map((o) => ({
      days: Array.isArray(o.days) ? (o.days as unknown[]).map((d) => Number(d)).filter((n) => Number.isFinite(n)) : [],
      start: asString(o.start),
      end: asString(o.end),
      price: asNumber(o.price),
    })) : undefined,
  }
}

export const normalizeOrderItem = (raw: unknown): OrderItem => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    productId: asString(getFirst(obj, ['productId', 'product_id'])),
    productName: asString(getFirst(obj, ['productName', 'product_name'])),
    quantity: asNumber(getFirst(obj, ['quantity'])),
    unitPrice: asNumber(getFirst(obj, ['unitPrice', 'unit_price'])),
    notes: asOptionalString(getFirst(obj, ['notes'])),
    status: normalizeStatus(getFirst(obj, ['status'])) as OrderItem['status'],
    addedAt: toDate(getFirst(obj, ['addedAt', 'added_at'])),
    readyAt: toOptionalDate(getFirst(obj, ['readyAt', 'ready_at'])),
    deliveredAt: toOptionalDate(getFirst(obj, ['deliveredAt', 'delivered_at'])),
  }
}

export const normalizeOrder = (raw: unknown): Order => {
  const obj = asObject(raw)
  const itemsValue = getFirst(obj, ['items', 'order_items'])
  const items = Array.isArray(itemsValue) ? itemsValue.map(normalizeOrderItem) : []
  return {
    id: asString(getFirst(obj, ['id'])),
    tableId: asString(getFirst(obj, ['tableId', 'table_id'])),
    reservationId: asOptionalString(getFirst(obj, ['reservationId', 'reservation_id'])),
    tableNumber: asNumber(getFirst(obj, ['tableNumber', 'table_number'])),
    clientName: asString(getFirst(obj, ['clientName', 'client_name'])),
    waiter: asString(getFirst(obj, ['waiter'])),
    diners: asOptionalNumber(getFirst(obj, ['diners', 'numDiners', 'num_diners', 'num_comensales'])),
    items,
    status: normalizeStatus(getFirst(obj, ['status'])) as Order['status'],
    createdAt: toDate(getFirst(obj, ['createdAt', 'created_at'])),
    updatedAt: toDate(getFirst(obj, ['updatedAt', 'updated_at'])),
    sentToKitchenAt: toOptionalDate(getFirst(obj, ['sentToKitchenAt', 'sent_to_kitchen_at'])),
    subtotal: asNumber(getFirst(obj, ['subtotal'])),
    tax: asNumber(getFirst(obj, ['tax'])),
    total: asNumber(getFirst(obj, ['total'])),
  }
}

export const normalizeUser = (raw: unknown): User => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    name: asString(getFirst(obj, ['name'])),
    email: asOptionalString(getFirst(obj, ['email'])),
    role: normalizeStatus(getFirst(obj, ['role'])) as User['role'],
    avatar: asOptionalString(getFirst(obj, ['avatar'])),
  }
}

export const normalizeStationPrinter = (raw: unknown): StationPrinterConfig => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    stationKey: asString(getFirst(obj, ['stationKey', 'station_key'])) as StationKey,
    enabled: asBoolean(getFirst(obj, ['enabled']), false),
    name: asOptionalString(getFirst(obj, ['name'])) ?? null,
    ip: asOptionalString(getFirst(obj, ['ip'])) ?? null,
    port: asNumber(getFirst(obj, ['port']), 9100),
    driver: asString(getFirst(obj, ['driver']), 'raw_tcp') as StationPrinterConfig['driver'],
    options: (getFirst(obj, ['options']) && typeof getFirst(obj, ['options']) === 'object' && !Array.isArray(getFirst(obj, ['options'])))
      ? (getFirst(obj, ['options']) as Record<string, unknown>)
      : null,
    version: asNumber(getFirst(obj, ['version']), 0),
  }
}

export const normalizeActivityLog = (raw: unknown): ActivityLog => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    timestamp: toDate(getFirst(obj, ['timestamp', 'created_at'])),
    actor: asString(getFirst(obj, ['actor'])),
    actorRole: normalizeStatus(getFirst(obj, ['actorRole', 'actor_role'])) as ActivityLog['actorRole'],
    entityType: asOptionalString(getFirst(obj, ['entityType', 'entity_type'])) as ActivityLog['entityType'],
    entityId: asOptionalString(getFirst(obj, ['entityId', 'entity_id'])),
    fromStatus: asOptionalString(getFirst(obj, ['fromStatus', 'from_status'])),
    toStatus: asOptionalString(getFirst(obj, ['toStatus', 'to_status'])),
    action: asString(getFirst(obj, ['action'])) as ActivityLog['action'],
    tableNumber: asOptionalNumber(getFirst(obj, ['tableNumber', 'table_number'])),
    details: asString(getFirst(obj, ['details'])),
  }
}

export const normalizeReservation = (raw: unknown): Reservation => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    tableId: asOptionalString(getFirst(obj, ['tableId', 'table_id'])) || null,
    tableNumber: asOptionalNumber(getFirst(obj, ['tableNumber', 'table_number'])) ?? null,
    zone: asOptionalString(getFirst(obj, ['zone'])) ?? null,
    reserverName: asString(getFirst(obj, ['reserverName', 'reserver_name'])),
    reserverPhone: asOptionalString(getFirst(obj, ['reserverPhone', 'reserver_phone'])),
    reserverEmail: asOptionalString(getFirst(obj, ['reserverEmail', 'reserver_email'])),
    partySize: asNumber(getFirst(obj, ['partySize', 'party_size']), 1),
    notes: asOptionalString(getFirst(obj, ['notes'])),
    reservedAt: toDate(getFirst(obj, ['reservedAt', 'reserved_at'])),
    preBlockMinutes: asNumber(getFirst(obj, ['preBlockMinutes', 'pre_block_minutes']), 15),
    unlockCodeHint: asOptionalString(getFirst(obj, ['unlockCodeHint', 'unlock_code_hint'])),
    kitchenLeadMinutes: asNumber(getFirst(obj, ['kitchenLeadMinutes', 'kitchen_lead_minutes']), 0),
    sendMode: asString(getFirst(obj, ['sendMode', 'send_mode']), 'auto') as Reservation['sendMode'],
    printTicketTiming: (asOptionalString(getFirst(obj, ['printTicketTiming', 'print_ticket_timing'])) || 'on_assign_or_due') as Reservation['printTicketTiming'],
    status: asString(getFirst(obj, ['status']), 'scheduled') as Reservation['status'],
    orderId: asOptionalString(getFirst(obj, ['orderId', 'order_id'])) || null,
    items: Array.isArray(obj.items) ? obj.items.map(normalizeOrderItem) : [],
    createdAt: toOptionalDate(getFirst(obj, ['createdAt', 'created_at'])),
  }
}

export const normalizeReservationSendRequest = (raw: unknown): import('@/lib/pos-types').ReservationSendRequest => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    reservationId: asString(getFirst(obj, ['reservationId', 'reservation_id'])),
    orderId: asString(getFirst(obj, ['orderId', 'order_id'])),
    tableId: asString(getFirst(obj, ['tableId', 'table_id'])),
    tableNumber: asNumber(getFirst(obj, ['tableNumber', 'table_number'])),
    reservedAt: toOptionalDate(getFirst(obj, ['reservedAt', 'reserved_at'])),
    reserverName: asString(getFirst(obj, ['reserverName', 'reserver_name'])),
    partySize: asNumber(getFirst(obj, ['partySize', 'party_size']), 1),
    status: asString(getFirst(obj, ['status']), 'pending') as import('@/lib/pos-types').ReservationSendRequest['status'],
    requestedBy: asString(getFirst(obj, ['requestedBy', 'requested_by'])),
    requestedAt: toDate(getFirst(obj, ['requestedAt', 'requested_at'])),
    decidedBy: asOptionalString(getFirst(obj, ['decidedBy', 'decided_by'])),
    decidedAt: toOptionalDate(getFirst(obj, ['decidedAt', 'decided_at'])),
    createdAt: toOptionalDate(getFirst(obj, ['createdAt', 'created_at'])),
  }
}

export const normalizeStationTicket = (raw: unknown): StationTicket => {
  const obj = asObject(raw)
  const itemIdsRaw = getFirst(obj, ['itemIds', 'item_ids'])
  const itemIds = Array.isArray(itemIdsRaw) ? itemIdsRaw.map((v) => asString(v)) : undefined
  return {
    id: asString(getFirst(obj, ['id'])),
    orderId: asString(getFirst(obj, ['orderId', 'order_id'])),
    tableNumber: asNumber(getFirst(obj, ['tableNumber', 'table_number'])),
    stationKey: asString(getFirst(obj, ['stationKey', 'station_key'])) as StationKey,
    round: asNumber(getFirst(obj, ['round']), 1),
    status: asString(getFirst(obj, ['status'])),
    printStatus: asOptionalString(getFirst(obj, ['printStatus', 'print_status'])) ?? undefined,
    printError: asOptionalString(getFirst(obj, ['printError', 'print_error'])) ?? undefined,
    printAttempts: typeof getFirst(obj, ['printAttempts', 'print_attempts']) === 'number'
      ? (getFirst(obj, ['printAttempts', 'print_attempts']) as number)
      : undefined,
    printedAt: asOptionalString(getFirst(obj, ['printedAt', 'printed_at'])) ? new Date(asString(getFirst(obj, ['printedAt', 'printed_at']))) : undefined,
    readyAt: asOptionalString(getFirst(obj, ['readyAt', 'ready_at'])) ? new Date(asString(getFirst(obj, ['readyAt', 'ready_at']))) : undefined,
    lastPrintAttemptAt: asOptionalString(getFirst(obj, ['lastPrintAttemptAt', 'last_print_attempt_at']))
      ? new Date(asString(getFirst(obj, ['lastPrintAttemptAt', 'last_print_attempt_at'])))
      : undefined,
    itemIds,
    createdAt: new Date(asString(getFirst(obj, ['createdAt', 'created_at']))),
  }
}

export const normalizePayment = (raw: unknown): Payment => {
  const obj = asObject(raw)
  return {
    id: asString(getFirst(obj, ['id'])),
    number_ticket: asString(getFirst(obj, ['number_ticket'])),
    orderId: asString(getFirst(obj, ['orderId', 'order_id'])),
    customerId: asOptionalString(getFirst(obj, ['customerId', 'customer_id'])),
    tableNumber: asNumber(getFirst(obj, ['tableNumber', 'table_number'])),
    client_name: asString(getFirst(obj, ['client_name'])),
    subtotal: asNumber(getFirst(obj, ['subtotal'])),
    tax: asNumber(getFirst(obj, ['tax'])),
    discount: asNumber(getFirst(obj, ['discount'])),
    tip: asNumber(getFirst(obj, ['tip'])),
    total: asNumber(getFirst(obj, ['total'])),
    method: normalizeStatus(getFirst(obj, ['method'])) as Payment['method'],
    status: normalizeStatus(getFirst(obj, ['status'])) as Payment['status'],
    paidAt: toOptionalDate(getFirst(obj, ['paidAt', 'paid_at'])),
    receipt: (() => {
      const receipt = getFirst(obj, ['receipt'])
      if (receipt === undefined) return undefined
      const r = asObject(receipt)
      if (!r) return receipt === null ? null : undefined
      const itemsValue = getFirst(r, ['items'])
      const items = Array.isArray(itemsValue)
        ? (itemsValue as unknown[]).map((it) => {
            const o = asObject(it)
            return {
              productName: asString(getFirst(o, ['productName', 'product_name'])),
              quantity: asNumber(getFirst(o, ['quantity'])),
              unitPrice: asNumber(getFirst(o, ['unitPrice', 'unit_price'])),
              notes: asOptionalString(getFirst(o, ['notes'])) ?? null,
            }
          })
        : undefined
      return {
        waiter: asOptionalString(getFirst(r, ['waiter'])) ?? undefined,
        items,
      }
    })(),
  }
}
